import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { useCandidates, useDictamenesList, useStandardsList } from "@/store/hooks";

type Filtro = "todos" | "competente" | "todavia-no" | "revocados";

export default function DictaminadorHistorial() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("dictaminador"), [setRole]);

  const dictamenes = useDictamenesList();
  const candidatesMap = useCandidates();
  const standards = useStandardsList();

  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [estandar, setEstandar] = useState<string>("todos");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return dictamenes
      .filter((d) => {
        if (filtro === "competente" && (d.resultado !== "competente" || d.revocacion)) return false;
        if (filtro === "todavia-no" && d.resultado !== "todavia-no") return false;
        if (filtro === "revocados" && !d.revocacion) return false;
        if (estandar !== "todos" && d.estandar !== estandar) return false;
        if (query.trim()) {
          const cand = candidatesMap[d.candidatoId];
          const hay =
            (cand?.nombre ?? "").toLowerCase().includes(query.toLowerCase()) ||
            d.expedienteId.toLowerCase().includes(query.toLowerCase()) ||
            (d.certificadoId ?? "").toLowerCase().includes(query.toLowerCase());
          if (!hay) return false;
        }
        return true;
      })
      .sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
  }, [dictamenes, filtro, estandar, query, candidatesMap]);

  return (
    <AppLayout pageTitle="Historial de dictámenes">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Historial de dictámenes</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{filtered.length} dictámenes registrados</div>
        </div>
        <Link href="/dictaminador" data-testid="link-back-bandeja">
          <PrimaryButton variant="outline">← Volver a la bandeja</PrimaryButton>
        </Link>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por candidato, expediente o folio..."
            data-testid="input-search"
            style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13 }}
          />
          <select value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)} data-testid="filter-resultado" style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white" }}>
            <option value="todos">Todos los resultados</option>
            <option value="competente">Competentes emitidos</option>
            <option value="todavia-no">Todavía no competente</option>
            <option value="revocados">Revocados</option>
          </select>
          <select value={estandar} onChange={(e) => setEstandar(e.target.value)} data-testid="filter-estandar" style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white" }}>
            <option value="todos">Todos los estándares</option>
            {standards.map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo}</option>)}
          </select>
        </div>
      </Card>

      <Card padding={0}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
          <SectionTitle>Dictámenes emitidos</SectionTitle>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
              {["Fecha", "Folio", "Candidato", "Estándar", "Resultado", "Justificación", ""].map((h) => (
                <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => {
              const cand = candidatesMap[d.candidatoId];
              const tone = d.revocacion ? "red" : d.resultado === "competente" ? "green" : "amber";
              const label = d.revocacion ? "Revocado" : d.resultado === "competente" ? "Competente" : "Todavía no";
              return (
                <tr key={d.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }} data-testid={`row-dict-${d.expedienteId}`}>
                  <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)" }}>{d.fecha.slice(0, 10)}</td>
                  <td style={{ padding: "10px 14px", fontFamily: "var(--app-font-mono)", color: "var(--cc-navy)", fontSize: 12 }}>{d.certificadoId ?? "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ fontWeight: 600, color: "var(--cc-navy)" }}>{cand?.nombre ?? d.candidatoId}</div>
                    <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Exp {d.expedienteId.toUpperCase()}</div>
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)" }}>{d.estandar}</td>
                  <td style={{ padding: "10px 14px" }}><Badge tone={tone as "green" | "red" | "amber"}>{label}</Badge></td>
                  <td style={{ padding: "10px 14px", color: "var(--cc-gray-600)", fontSize: 12, maxWidth: 280 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.fundamento}>{d.fundamento}</div>
                    {d.revocacion && <div style={{ fontSize: 10, color: "var(--cc-red)", marginTop: 2 }}>Revocado: {d.revocacion.motivo}</div>}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {d.resultado === "competente" ? (
                      <Link href={`/dictaminador/certificado/${d.expedienteId}`} data-testid={`link-cert-${d.expedienteId}`} style={{ color: "var(--cc-teal)", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Ver →</Link>
                    ) : (
                      <Link href={`/dictaminador/expediente/${d.expedienteId}`} data-testid={`link-exp-${d.expedienteId}`} style={{ color: "var(--cc-teal)", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Abrir →</Link>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: "30px 14px", textAlign: "center", fontSize: 13, color: "var(--cc-gray-500)" }}>
                  Sin dictámenes que coincidan con los filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </AppLayout>
  );
}
